import { useCallback, useEffect, useId, useRef, useState } from "react";

import { usePreviewRunEventQuery } from "@/api/previewInsights";
import {
  measureReplyText,
  summarizeTextMeasures,
  type LabRunModel,
} from "@/lib/labRunModel";
import { readContentSlot, readInlineBody } from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState } from "@/ui/states";

/**
 * Metrics, as Reliv's compare/metrics.py computes them. The text-free figures
 * come from the model. The four that need reply text (language leaks, hotline
 * flag, average reply length, questions per reply) are measured only after an
 * explicit press: each Liv reply is read once through the detail endpoint,
 * reduced to four numbers inside `TurnTextProbe`, and the text is dropped.
 * Only the numbers leave the probe, so no text is held in state, logged or put
 * in the address.
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

type Measure = ReturnType<typeof measureReplyText>;

/** Probes mounted at once: react-query bounds the requests, this bounds the burst. */
const BATCH = 5;

function Stat({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Reads ONE turn's Liv reply, reports its four counts, renders nothing.
 * A reply that is withheld, omitted, structured or unreadable reports `null`
 * ("not measured"), never zeros.
 */
function TurnTextProbe({
  projectId,
  previewRunRef,
  turnNumber,
  eventRef,
  onMeasured,
}: {
  projectId: string;
  previewRunRef: string;
  turnNumber: number;
  eventRef: string;
  onMeasured: (turnNumber: number, measure: Measure | null) => void;
}) {
  const detail = usePreviewRunEventQuery(projectId, previewRunRef, eventRef, {
    enabled: true,
  });
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || (!detail.isSuccess && !detail.isError)) {
      return;
    }
    reported.current = true;
    let measure: Measure | null = null;
    if (detail.isSuccess) {
      const event: unknown = detail.data?.event;
      const body =
        typeof event === "object" && event !== null
          ? (event as Record<string, unknown>)["detail"]
          : undefined;
      const slot =
        typeof body === "object" && body !== null && !Array.isArray(body)
          ? readContentSlot((body as Record<string, unknown>)["livResponse"])
          : null;
      const text = slot === null ? null : readInlineBody(slot);
      measure =
        text !== null && text.kind === "text"
          ? measureReplyText(text.text)
          : null;
    }
    onMeasured(turnNumber, measure);
  }, [detail.isSuccess, detail.isError, detail.data, turnNumber, onMeasured]);
  return null;
}

function TextMeasures({
  projectId,
  model,
}: {
  projectId: string;
  model: LabRunModel;
}) {
  const [requested, setRequested] = useState(false);
  const [measures, setMeasures] = useState<ReadonlyMap<number, Measure | null>>(
    new Map(),
  );
  const turns = model.metrics.textTurns;

  const onMeasured = useCallback(
    (turnNumber: number, measure: Measure | null) => {
      setMeasures((current) => new Map(current).set(turnNumber, measure));
    },
    [],
  );

  // Batches: the next five probes mount once every probe mounted so far reported.
  const reportedCount = measures.size;
  const mountedCount = requested
    ? Math.min(turns.length, (Math.floor(reportedCount / BATCH) + 1) * BATCH)
    : 0;
  const measured = [...measures.values()].filter(
    (m): m is Measure => m !== null,
  );
  const notMeasured = measures.size - measured.length;
  const done = requested && reportedCount >= turns.length;
  const summary = summarizeTextMeasures(measured);

  return (
    <Card>
      <CardHeader>
        <CardTitle>From conversation text</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Reads each Liv reply once through the detail endpoint, keeps only the
          counts, and never keeps the text.
        </p>
        {turns.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No turn carries a Liv reply to measure.
          </p>
        ) : !requested ? (
          <Button onClick={() => setRequested(true)}>Measure reply text</Button>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Measured {measured.length} of {turns.length} turns
            {notMeasured > 0
              ? ` (${notMeasured} not measured: text withheld or unreadable)`
              : ""}
            {done ? "." : "…"}
          </p>
        )}
        {requested
          ? turns
              .slice(0, mountedCount)
              .map((turn) => (
                <TurnTextProbe
                  key={turn.eventRef}
                  projectId={projectId}
                  previewRunRef={model.previewRunRef}
                  turnNumber={turn.turnNumber}
                  eventRef={turn.eventRef}
                  onMeasured={onMeasured}
                />
              ))
          : null}
        {requested && measured.length > 0 ? (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Average reply length">
              {summary.avgReplyChars === null
                ? "not measured"
                : `${summary.avgReplyChars} characters`}
            </Stat>
            <Stat label="Questions per reply">
              {summary.questionsPerReply === null
                ? "not measured"
                : summary.questionsPerReply}
            </Stat>
            <Stat label="Language leaks">
              {summary.languageLeaks === null
                ? "not measured"
                : summary.languageLeaks}
            </Stat>
            <Stat label="Hotline flag">
              {summary.hotlineFlag === null
                ? "not measured"
                : summary.hotlineFlag
                  ? "Raised"
                  : "Not raised"}
            </Stat>
          </dl>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function LabMetricsTab({ projectId, model }: Props) {
  const headingId = useId();
  const metrics = model.metrics;

  if (model.lab.turns.length === 0) {
    return (
      <section aria-labelledby={headingId}>
        <h2
          id={headingId}
          className="mb-3 text-base font-semibold text-foreground"
        >
          Metrics
        </h2>
        <EmptyState
          title="No turns yet"
          description="Metrics appear once the run has recorded turns."
        />
      </section>
    );
  }

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-base font-semibold text-foreground">
        Metrics
      </h2>

      <Card>
        <CardHeader>
          <CardTitle>From the trace</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Turns">{metrics.turns}</Stat>
            <Stat label="Minutes">
              {metrics.minutes === null
                ? "not recorded"
                : metrics.minutes.toFixed(1)}
            </Stat>
            <Stat label="Repeated replies">{metrics.repeats}</Stat>
            <Stat label="Mean reply latency">
              {metrics.meanReplyLatencyMs === null
                ? "not recorded"
                : `${Math.round(metrics.meanReplyLatencyMs)} ms`}
            </Stat>
          </dl>
          <div>
            <h3 className="text-xs font-medium text-muted-foreground">
              Tracks visited
            </h3>
            {metrics.tracksVisited.length === 0 ? (
              <p className="text-sm text-muted-foreground">none recorded</p>
            ) : (
              <ol className="mt-1 flex flex-wrap gap-2">
                {metrics.tracksVisited.map((track, index) => (
                  <li key={`${index}-${track}`}>
                    <Badge tone="outline">
                      {index + 1}. {track}
                    </Badge>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div>
            <h3 className="text-xs font-medium text-muted-foreground">
              Substeps by track
            </h3>
            {metrics.substepCounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No criterion recorded.
              </p>
            ) : (
              <div className="mt-1 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs text-muted-foreground">
                      <th
                        scope="col"
                        className="py-1 pe-3 text-start font-medium"
                      >
                        Track
                      </th>
                      <th
                        scope="col"
                        className="py-1 pe-3 text-start font-medium"
                      >
                        passed
                      </th>
                      <th scope="col" className="py-1 text-start font-medium">
                        failed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.substepCounts.map((row) => (
                      <tr
                        key={row.track}
                        className="border-b border-border last:border-0"
                      >
                        <td className="py-1 pe-3">{row.track}</td>
                        <td className="py-1 pe-3">{row.passed}</td>
                        <td className="py-1">{row.failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <TextMeasures
        key={model.previewRunRef}
        projectId={projectId}
        model={model}
      />
    </section>
  );
}
